/**
 * Google Analytics / Ads hooks. The gtag snippet in index.html only exists on app.gizmodata.com
 * (GizmoData Cloud), so everything here is a no-op anywhere else — Cloud-at-Customer, dev, QA.
 */
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

// A signed-in account created this recently is a fresh sign-up (Clerk just finished it).
const FRESH_SIGNUP_WINDOW_MS = 15 * 60 * 1000;
const trackedKey = (userId: string) => `gizmodata.signupTracked.${userId}`;

/** How the account was created: the SSO provider Clerk reports ('google', 'microsoft', 'apple',
 *  'github', …) when the sign-up went through one, else 'email'. */
export const signUpMethod = (externalAccounts?: { provider: string }[] | null): string =>
  externalAccounts?.[0]?.provider || 'email';

/**
 * Google Ads `sign_up` conversion, once per new account, right after Clerk completes the sign-up,
 * with the real sign-up method (GA4's `method` parameter). Returns whether it fired.
 */
export const trackSignUpOnce = (
  user: { id: string; createdAt: Date | null; externalAccounts?: { provider: string }[] | null } | null | undefined,
  now: number = Date.now(),
): boolean => {
  if (!user?.createdAt || typeof window.gtag !== 'function') return false;
  if (now - user.createdAt.getTime() > FRESH_SIGNUP_WINDOW_MS) return false;
  try {
    if (localStorage.getItem(trackedKey(user.id))) return false;
    localStorage.setItem(trackedKey(user.id), new Date(now).toISOString());
  } catch {
    // No storage (private mode): still report it — at worst once per page load in that window.
  }
  window.gtag('event', 'sign_up', { method: signUpMethod(user.externalAccounts) });
  return true;
};
